'use client';

import type { CSSProperties, FC, PropsWithChildren, ReactNode } from 'react';
import { cx } from '../../utils/cx';
import type { ResponsiveProperty } from '../../utils/responsive';
import {
  isResponsiveObject,
  lengthValue,
  resolveResponsive,
} from '../../utils/responsive';
import type {
  DisplayProps,
  MarginProps,
  MaxWidthProps,
  PaddingProps,
} from '../../utils/spacing';
import styles from './Box.module.css';

export interface BoxProps
  extends DisplayProps, MarginProps, PaddingProps, MaxWidthProps {
  className?: string;
  id?: string;
  aspectRatio?: ResponsiveProperty<CSSProperties['aspectRatio']>;
  height?: ResponsiveProperty<CSSProperties['height']>;
  minHeight?: ResponsiveProperty<CSSProperties['minHeight']>;
  children?: ReactNode;
}

type Vars = Record<string, string | undefined>;

const spacingVars = (
  name: string,
  side: ResponsiveProperty<number | string> | undefined,
  shorthand: ResponsiveProperty<number | string> | undefined
): Vars => {
  const value = side ?? shorthand;
  if (value === undefined) return {};
  if (isResponsiveObject(value)) {
    return {
      [`--box-${name}-phone`]: lengthValue(value.phone),
      [`--box-${name}-tablet`]: lengthValue(value.tablet),
      [`--box-${name}-desktop`]: lengthValue(value.desktop),
    };
  }
  return { [`--box-${name}`]: lengthValue(value) };
};

export const Box: FC<PropsWithChildren<BoxProps>> = ({
  className,
  id,
  children,
  display,
  margin,
  marginTop,
  marginRight,
  marginBottom,
  marginLeft,
  padding,
  paddingTop,
  paddingRight,
  paddingBottom,
  paddingLeft,
  maxWidth,
  height,
  minHeight,
  aspectRatio,
}) => {
  const style: Record<string, string | number | undefined> = {
    ...spacingVars('mt', marginTop, margin),
    ...spacingVars('mr', marginRight, margin),
    ...spacingVars('mb', marginBottom, margin),
    ...spacingVars('ml', marginLeft, margin),
    ...spacingVars('pt', paddingTop, padding),
    ...spacingVars('pr', paddingRight, padding),
    ...spacingVars('pb', paddingBottom, padding),
    ...spacingVars('pl', paddingLeft, padding),
    display: resolveResponsive(display),
    maxWidth: lengthValue(maxWidth),
    height: lengthValue(resolveResponsive(height)),
    minHeight: lengthValue(resolveResponsive(minHeight)),
    aspectRatio: resolveResponsive(aspectRatio) as string | number | undefined,
  };

  return (
    <div
      className={cx(styles.box, className)}
      id={id}
      style={style as CSSProperties}
    >
      {children}
    </div>
  );
};
